import React, { useCallback, useEffect, useState } from "react";
import { Close } from "@carbon/react/icons";
import { styled } from "styled-components";
import { useOnClickOutside } from "../helpers";
import { FormLabel, HeaderGlobalAction, HeaderMenuItem, HeaderSideNavItems, Modal, SideNavItems, SideNavLink, SideNavMenu, SideNavMenuItem, } from "@carbon/react";
import { C3AppMenuIcon } from "../../../assets/c3-icons";
import { useC3Profile } from "../../c3-user-configuration/c3-profile-provider/c3-profile-provider";
import { useC3UserConfiguration } from "../../c3-user-configuration/c3-user-configuration-provider";
import { APPS } from "../../../utils/camunda";
import { NavWrapper, SideNav } from "./components";
import { useClusterUpdate } from "../../../contexts/c3-cluster-update-manager";
const getOrgLink = (app, orgId, domain) => {
    switch (app) {
        case "console": {
            return `https://console.${domain}/org/${orgId}`;
        }
        case "modeler": {
            return `https://modeler.${domain}/login?returnUrl=/org/${orgId}`;
        }
        default: {
            return "#";
        }
    }
};
const getClustersToRender = (clusters, activeOrg, appToNavigateTo) => {
    return clusters?.filter(({ status, endpoints, generation }) => status[appToNavigateTo === "identity" ? "zeebe" : appToNavigateTo] ===
        "Healthy" &&
        endpoints[appToNavigateTo] &&
        // either the user has appreadpermissions on this app
        (activeOrg?.permissions?.cluster?.[appToNavigateTo]?.read ||
            //  OR this is a 8.8+ cluster - in this case we also allow rendering this item
            [1, 2, 3, 4, 5, 6, 7].every((pre8Minor) => !generation.versions?.zeebe?.includes(`8.${pre8Minor}`))));
};
const OrgPickerModal = ({ isOpen, onCancel, orgs, activeOrg, loadingStatus, appToNavigateTo, domain, }) => {
    const hasNoTeams = Object.values(orgs ?? {})?.every(({ org }) => {
        return !org?.name;
    });
    const hasNoTeamsWithClusters = Object.values(orgs ?? {})?.every(({ clusters }) => {
        const isConsoleOrModeler = [APPS[0], APPS[1]].includes(appToNavigateTo);
        return (!isConsoleOrModeler &&
            getClustersToRender(clusters, activeOrg, appToNavigateTo).length === 0);
    });
    return (React.createElement(Modal, { danger: true, open: isOpen, modalHeading: "Select a team", onRequestClose: onCancel, passiveModal: true, loadingStatus: loadingStatus, size: "sm" },
        React.createElement("div", { style: { marginLeft: "-1rem" } },
            React.createElement(FormLabel, { style: {
                    paddingTop: "15px",
                    paddingLeft: "1rem",
                } }, hasNoTeams
                ? "No teams found"
                : hasNoTeamsWithClusters
                    ? "No teams with clusters found"
                    : "Teams"),
            React.createElement("div", { style: {
                    marginTop: "-1rem",
                } },
                React.createElement(SideNavItems, null, Object.values(orgs ?? {})?.map(({ org, clusters }) => {
                    const isConsoleOrModeler = [APPS[0], APPS[1]].includes(appToNavigateTo);
                    const toOrg = getOrgLink(appToNavigateTo, org.uuid, domain ?? "");
                    if (isConsoleOrModeler) {
                        return (React.createElement(SideNavLink, { key: org?.uuid, href: toOrg }, org?.name));
                    }
                    const clustersToRender = getClustersToRender(clusters, activeOrg, appToNavigateTo);
                    if (clustersToRender.length === 0) {
                        return null;
                    }
                    return (React.createElement(SideNavMenu, { key: org?.uuid, title: org?.name }, clustersToRender?.map((cluster) => {
                        return (React.createElement(SideNavMenuItem, { key: cluster.uuid, href: cluster.endpoints[appToNavigateTo] }, cluster.name));
                    })));
                }))))));
};
const SubElementWrapper = styled.ul `
	.cds--side-nav__submenu[aria-expanded="true"] + .cds--side-nav__menu {
		max-block-size: 456px;
		overflow-y: auto;
		visibility: inherit;
	}
`;
export const C3NavigationAppBar = ({ app: appProps, appBar, forwardRef, navbar, toggleAppbar, }) => {
    const { currentApp, domain, analyticsTrack, currentClusterUuid } = useC3UserConfiguration();
    const { orgs, clusters, setClusters, activeOrg, isEnabled, loadClustersById, } = useC3Profile();
    const { setPanelRef: panelRef, setIconRef: iconRef } = useOnClickOutside(() => toggleAppbar(false));
    const [appElements, setAppElements] = useState([]);
    const [loadingStatus, setLoadingStatus] = useState("inactive");
    const [clustersByOrgId, setClustersByOrgId] = useState(null);
    const [appToNavigateTo, setAppToNavigateTo] = useState(APPS[0]);
    useClusterUpdate({
        onUpdate: (data) => {
            if (data && data.org && data.org === activeOrg?.uuid) {
                switch (data.entity) {
                    case "cluster": {
                        if (data.payload.uuid) {
                            const clusterDto = data.payload;
                            const status = clusterDto.status;
                            const endpoints = {
                                tasklist: status?.tasklistUrl,
                                operate: status?.operateUrl,
                                optimize: status?.optimizeUrl,
                                console: "",
                                modeler: "",
                                identity: "",
                            };
                            const expectedStatus = {
                                tasklist: status?.tasklistStatus,
                                operate: status?.operateStatus,
                                optimize: status?.optimizeStatus,
                                console: "",
                                modeler: "",
                                identity: "",
                                zeebe: "",
                            };
                            const affectedCluster = {
                                uuid: clusterDto.uuid,
                                name: clusterDto.name,
                                status: expectedStatus,
                                generation: clusterDto.generation,
                                endpoints,
                            };
                            switch (data.type) {
                                case "deleted": {
                                    setClusters((clusters) => clusters?.filter((cluster) => cluster.uuid !== affectedCluster.uuid) ?? []);
                                    break;
                                }
                                case "updated": {
                                    setClusters((clusters) => {
                                        if (!clusters) {
                                            return [];
                                        }
                                        const clustersClone = [...clusters];
                                        const clusterToUpdateIndex = clustersClone?.findIndex((cluster) => cluster.uuid === affectedCluster.uuid);
                                        if (clusterToUpdateIndex > -1) {
                                            clustersClone[clusterToUpdateIndex] = affectedCluster;
                                            return clustersClone;
                                        }
                                        return clusters;
                                    });
                                }
                                case "created": {
                                    setClusters((clusters) => {
                                        if (!clusters) {
                                            return [];
                                        }
                                        const clustersClone = [...clusters];
                                        const clusterToUpdateIndex = clustersClone?.findIndex((cluster) => cluster.uuid === affectedCluster.uuid);
                                        if (clusterToUpdateIndex > -1) {
                                            clustersClone[clusterToUpdateIndex] = affectedCluster;
                                        }
                                        else {
                                            clustersClone.push(affectedCluster);
                                        }
                                        return clustersClone;
                                    });
                                    break;
                                }
                                default:
                                    break;
                            }
                        }
                        break;
                    }
                    default: {
                        break;
                    }
                }
            }
        },
    });
    if (!appBar.elements && !isEnabled)
        console.warn("No app elements and user config provided. Please provide at least one of them.");
    const isMemberOfTeam = useCallback((orgUuid) => orgs?.some(({ uuid }) => uuid === orgUuid) || false, [orgs]);
    const getClustersForSuperOrg = useCallback(async () => {
        const updatedClusterByOrgId = {};
        await Promise.all(activeOrg?.childOrganizations
            ?.filter((team) => isMemberOfTeam(team.uuid))
            .map((org) => loadClustersById(org.uuid)?.then(({ result }) => {
            updatedClusterByOrgId[org.uuid] = {
                org,
                clusters: result ?? [],
            };
        })) ?? []);
        setClustersByOrgId(updatedClusterByOrgId);
    }, [activeOrg, isMemberOfTeam]);
    const handleSuperOrg = useCallback(() => {
        if (!activeOrg?.uuid) {
            return;
        }
        setIsOrgPickerModalOpen(true);
        if (clustersByOrgId) {
            return;
        }
        setLoadingStatus("active");
        getClustersForSuperOrg()
            .catch((error) => console.error(error))
            .finally(() => {
            setLoadingStatus("finished");
        });
    }, [clustersByOrgId, activeOrg, getClustersForSuperOrg]);
    useEffect(() => {
        if (activeOrg?.isSuperOrg) {
            getClustersForSuperOrg()
                .catch((error) => console.error(error))
                .finally(() => {
                setLoadingStatus("finished");
            });
        }
    }, [activeOrg, getClustersForSuperOrg]);
    useEffect(() => {
        if (appBar.elements)
            return;
        const defaultElements = [];
        const enforceDropDown = !APPS.filter((app) => app !== "console" && app !== "modeler").every((app) => getClustersToRender(clusters ?? [], activeOrg, app).length < 2);
        for (const app of APPS) {
            if (!domain || !currentApp || !activeOrg?.uuid)
                return;
            const element = {
                key: app,
                label: app.charAt(0).toUpperCase() + app.slice(1),
                active: currentApp === app,
                href: currentApp === app ? "#" : undefined,
                onClick: currentApp === app ? () => false : undefined,
            };
            if (app === currentApp &&
                ["console", "modeler"].includes(app) &&
                appProps.routeProps) {
                element.routeProps = appProps.routeProps;
            }
            else if (app === "console") {
                element.href = `https://console.${domain}/org/${activeOrg?.uuid}`;
            }
            else if (app === "modeler") {
                const hasPermission = activeOrg?.permissions?.org?.webide?.read;
                if (hasPermission) {
                    element.href = `https://modeler.${domain}/login?returnUrl=/org/${activeOrg?.uuid}`;
                }
            }
            else {
                const hasAppReadPermission = activeOrg?.permissions?.cluster?.[app]?.read;
                const clustersToRender = getClustersToRender(clusters ?? [], activeOrg, app);
                if (clustersToRender?.length === 1 && !enforceDropDown) {
                    if (currentApp !== app)
                        element.href = clustersToRender?.[0].endpoints[app];
                }
                else if (clustersToRender?.length > 1 ||
                    (enforceDropDown && clustersToRender?.length > 0)) {
                    element.onClick = undefined;
                    element.subElements = clustersToRender.map((cluster, index) => ({
                        key: `${app}-${cluster.uuid}-${index}`,
                        label: cluster.name,
                        href: cluster.endpoints[app],
                        onClick: () => {
                            toggleAppbar(false);
                            analyticsTrack?.(`${app}:open`, { currentApp: app });
                        },
                        isActive: currentApp === app && currentClusterUuid === cluster.uuid,
                    }));
                }
                else if (app === "identity") {
                    // no app-teaser for identity for now
                    continue;
                }
                else if (hasAppReadPermission) {
                    if (appBar.appTeaserRouteProps?.[app]) {
                        element.routeProps = appBar.appTeaserRouteProps?.[app];
                    }
                    else {
                        element.href = `https://${currentApp}.${domain}/org/${activeOrg?.uuid}/appTeaser/${app}`;
                    }
                }
            }
            if (element.href || element.routeProps || element.subElements) {
                if (activeOrg.isSuperOrg) {
                    element.href = "#";
                    element.routeProps = null;
                    element.onClick = () => {
                        setAppToNavigateTo(app);
                        handleSuperOrg();
                        // probs want to track?
                        // analyticsTrack?.(`${app}:open`, { currentApp: app })
                    };
                }
                defaultElements.push(element);
            }
        }
        setAppElements(defaultElements);
    }, [clusters, currentApp, domain, handleSuperOrg]);
    const appBarElements = appBar.elements || (clusters || clustersByOrgId ? appElements : null);
    const [isOrgPickerModalOpen, setIsOrgPickerModalOpen] = useState(false);
    return (React.createElement(React.Fragment, null,
        React.createElement(HeaderGlobalAction
        // eslint-disable-next-line
        // @ts-ignore
        , { 
            // eslint-disable-next-line
            // @ts-ignore
            ref: iconRef, "aria-label": "Camunda components", isActive: appBar.isOpen, onClick: () => {
                toggleAppbar(!appBar.isOpen);
            }, tooltipAlignment: "start", 
            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
            /* @ts-ignore */
            leaveDelayMs: 100 }, appBar.isOpen ? React.createElement(Close, { size: 20 }) : React.createElement(C3AppMenuIcon, { size: 20 })),
        React.createElement(OrgPickerModal, { activeOrg: activeOrg, appToNavigateTo: appToNavigateTo, isOpen: isOrgPickerModalOpen, orgs: clustersByOrgId, onCancel: () => {
                setIsOrgPickerModalOpen(false);
            }, loadingStatus: loadingStatus, domain: domain }),
        React.createElement(NavWrapper, null,
            React.createElement(SideNav, { ref: panelRef, "aria-label": appBar.ariaLabel || "Side Navigation", expanded: appBar.isOpen, isPersistent: false },
                React.createElement(SideNavItems, null,
                    React.createElement("li", null, navbar.elements.length > 0 && (React.createElement(HeaderSideNavItems, { hasDivider: true }, navbar.elements.map((element) => (React.createElement(HeaderMenuItem, { key: element.key, as: element.routeProps && forwardRef, isActive: element.isCurrentPage, ...element.routeProps, onClick: () => {
                            if (element.routeProps.onClick) {
                                element.routeProps.onClick();
                            }
                            if (appBar.closeOnClick !== false) {
                                toggleAppbar(false);
                            }
                        } }, element.label)))))),
                    React.createElement("li", null,
                        React.createElement(SubElementWrapper, { "$expanded": appBar.isOpen }, appBarElements &&
                            appBarElements.map((element) => {
                                if (element.subElements && element.subElements.length > 0) {
                                    return (React.createElement(SideNavMenu, { large: true, title: element.label, key: element.key }, element.subElements.map((subElement) => (React.createElement(SideNavMenuItem, { as: subElement.routeProps && forwardRef, key: subElement.key, href: subElement.href, target: subElement.href ? subElement.target : undefined, isActive: subElement.isActive || subElement.active, ...subElement.routeProps, onClick: () => {
                                            if (subElement.onClick) {
                                                subElement.onClick();
                                            }
                                            if (subElement.routeProps?.onClick) {
                                                subElement.routeProps.onClick();
                                            }
                                            if (appBar.closeOnClick !== false) {
                                                toggleAppbar(false);
                                            }
                                            if (appBar.elementClicked) {
                                                appBar.elementClicked(subElement.key);
                                            }
                                        } }, subElement.label)))));
                                }
                                else {
                                    return (React.createElement(SideNavLink, { as: element.routeProps && forwardRef, key: element.key, large: true, isActive: element.active, ...element.routeProps, onClick: () => {
                                            if (element.onClick) {
                                                element.onClick();
                                            }
                                            if (element.routeProps?.onClick) {
                                                element.routeProps.onClick();
                                            }
                                            if (appBar.closeOnClick !== false) {
                                                toggleAppbar(false);
                                            }
                                            if (appBar.elementClicked) {
                                                appBar.elementClicked(element.key);
                                            }
                                        }, href: element.href, target: element.href ? element.target : undefined }, element.label));
                                }
                            }))))))));
};
